import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Login from './Login';
import {
  clearSession, consumeAuthFragment, fetchAccount,
  getToken, getUser, setCachedAccount, setToken, setUser,
} from '../api';

// What the API can put in the fragment, in words. Anything unlisted is a bug on
// our side rather than something the user did, so it gets the generic line.
const SIGN_IN_ERRORS = {
  expired: 'That sign-in took too long and expired. Please try again.',
  auth:    'Steam could not verify that sign-in. Please try again.',
  server:  'Something went wrong finishing your sign-in. Please try again.',
};

function AuthPage() {
  const navigate = useNavigate();
  // true while working out whether anyone is already signed in; the login form
  // only renders once that answer is no.
  const [checking, setChecking] = useState(true);
  const [steamError, setSteamError] = useState('');

  // Signing in leads to the app, always. Whether a Steam account is connected
  // is the Inventory tab's business, not a condition of getting in.
  const goToApp = useCallback(() => {
    navigate('/app', { replace: true });
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // 1) Coming back from Steam. The fragment is read and scrubbed in one go.
      const fragment = consumeAuthFragment();

      if (fragment.error) {
        if (!cancelled) {
          setSteamError(SIGN_IN_ERRORS[fragment.error] || SIGN_IN_ERRORS.server);
          setChecking(false);
        }
        return;
      }

      const fromSteam = Boolean(fragment.token && fragment.user);

      if (fromSteam) {
        setToken(fragment.token);
        setUser(fragment.user);
      } else if (!getToken() || !getUser()) {
        // 2) Nobody is signed in and Steam did not just send us anyone.
        if (!cancelled) setChecking(false);
        return;
      }

      // 3) Either way we now hold a token. Confirm it is still worth something
      //    before sending them in, and cache the account so the Inventory tab
      //    can paint its Steam panel without a round trip of its own.
      let account;
      try {
        account = await fetchAccount();
      } catch {
        if (cancelled) return;
        // The server did not say no, it said nothing. Signing the user out over
        // a network blip would be the wrong read, so go in on the strength of
        // the token we hold; the app has its own retry and error surface.
        goToApp();
        return;
      }
      if (cancelled) return;

      if (!account) {
        clearSession();
        setChecking(false);
        return;
      }
      goToApp();
    })();

    return () => { cancelled = true; };
  }, [goToApp]);

  const handleLogin = (username, account) => {
    setUser(username);
    setCachedAccount(account || null);
    goToApp();
  };

  if (checking) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-[#0a0c12]">
        <div className="size-8 animate-spin rounded-full border-2 border-white/15 border-t-white/70" />
      </div>
    );
  }

  return <Login onLogin={handleLogin} steamError={steamError} />;
}

export default AuthPage;
